import React from 'react';
import { Link } from 'react-router-dom';
import LocalHelmet from '../common/LocalHelmet';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowRight, FiTarget, FiBarChart, FiCpu, FiTrendingUp, FiUsers, FiZap, FiShield, FiCheckCircle } = FiIcons;

const Services = () => {
  const services = [
    {
      icon: FiTarget,
      title: 'Growth Strategy Advisory',
      subtitle: 'Guidance for boards, CMOs, and CEOs on profitable growth and investment clarity',
      description: 'You make the hard calls. We help you defend them — in the boardroom, with your team, and when everything’s in flux. One client cut wasted spend by 20%. Another made faster choices when the market shifted.',
      features: [
        'Board-ready growth frameworks',
        'Investment prioritization models',
        'Scenario planning and risk assessment',
        'Strategic roadmap development',
        'Executive decision support'
      ],
      outcomes: 'Stronger margins, clearer investment decisions, higher board confidence'
    },
    {
      icon: FiBarChart,
      title: 'Strategic Measurement',
      subtitle: 'Build and scale decision systems that tie investment to outcomes',
      description: 'We build measurement frameworks that prove what works through attribution, incrementality, marketing mix modeling, forecasting, and more. No vanity metrics just clarity on what drives growth.',
      features: [
        'Marketing Mix Modeling (MMM)',
        'Incrementality testing and measurement',
        'Attribution analysis and optimization',
        'Predictive forecasting models',
        'Custom dashboard development'
      ],
      outcomes: 'ROI improvements of 12-20%, planning cycles cut by 35%'
    },
    {
      icon: FiCpu,
      title: 'AI in Practice',
      subtitle: 'Thoughtful adoption of AI platforms to accelerate insights and scale creation',
      description: 'Practical AI implementation using ChatGPT, Claude, Gemini, Perplexity, and custom GPTs to enhance your strategic capabilities without the hype.',
      features: [
        'Custom GPT development',
        'AI-powered insight generation',
        'Automated report and RFP responses',
        'Scalable content creation workflows',
        'AI strategy integration planning'
      ],
      outcomes: 'Faster insights, scalable processes, competitive advantage'
    },
    {
      icon: FiTrendingUp,
      title: 'Data in Action',
      subtitle: 'Turn complex data into clear decisions with frameworks designed for execution',
      description: 'We don\'t just analyze data, we turn it into frameworks designed for speed, clarity, and real-world execution. Your data becomes a decision-making engine, not a reporting burden.',
      features: [
        'Executive decision dashboards',
        'Automated insight generation',
        'Real-time performance tracking',
        'Scenario modeling and planning',
        'Cross-functional data integration'
      ],
      outcomes: 'Faster decision-making, reduced analysis paralysis, improved execution'
    },
    {
      icon: FiUsers,
      title: 'Fractional Leadership',
      subtitle: 'Interim or part-time executive support during transformation',
      description: 'Step-in leadership when you need executive strategy coverage, stability during transitions, or specialized expertise for PE-backed growth initiatives.',
      features: [
        'Interim C-suite coverage',
        'Transformation leadership',
        'Team scaling and development',
        'PE portfolio company support',
        'Strategic project management'
      ],
      outcomes: 'Seamless transitions, accelerated growth, organizational stability'
    }
  ];

  const approach = [
    {
      icon: FiBarChart,
      title: 'Data-Driven',
      description: 'Every recommendation backed by rigorous analysis and real business outcomes.'
    },
    {
      icon: FiZap,
      title: 'Action-Oriented',
      description: 'Frameworks designed to be used, not filed away. Living tools that evolve with your business.'
    },
    {
      icon: FiShield,
      title: 'Values-Aligned',
      description: 'We only work with clients who share our commitment to inclusion, equity, and humanity.'
    },
    {
      icon: FiCheckCircle,
      title: 'Results-Focused',
      description: 'Measured success through your growth, not our deliverables. Your wins are our wins.'
    }
  ];

  return (
    <>
      <LocalHelmet prioritizeSeoTags>
        <title>Growth Strategy Services | Richardson Strategy Group</title>
        <meta
          name="description"
          content="Explore growth strategy, analytics, measurement, AI programs, and fractional executive leadership. Richardson Strategy Group builds solutions for real results."
        />
        <link rel="canonical" href="https://richardsonstrategy.group/services" />
        {/* Open Graph */}
        <meta property="og:site_name" content="Richardson Strategy Group" />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Growth Strategy Services | Richardson Strategy Group"
        />
        <meta
          property="og:description"
          content="Growth, measurement, analytics, and AI services built for executives who want clarity and progress without the jargon."
        />
        <meta property="og:url" content="https://richardsonstrategy.group/services" />
        <meta
          property="og:image"
          content="https://raw.githubusercontent.com/richardsonstrategygroup/rsg-assets/main/social/og-default.jpg"
        />
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Growth Strategy Services | Richardson Strategy Group"
        />
        <meta
          name="twitter:description"
          content="Strategy and analytics services for real results. Coaching, measurement, and leadership—no jargon, just impact."
        />
        <meta
          name="twitter:image"
          content="https://raw.githubusercontent.com/richardsonstrategygroup/rsg-assets/main/social/twitter-card.jpg"
        />
        {/* JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Service',
            serviceType: 'Growth Strategy & Analytics',
            provider: {
              '@type': 'Organization',
              name: 'Richardson Strategy Group'
            },
            areaServed: 'Global',
            url: 'https://richardsonstrategy.group/services',
            description:
              'Growth strategy, measurement, analytics, and AI programs for executives, boards, and fast teams.',
          })}
        </script>
      </LocalHelmet>


      <div className="min-h-screen py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero */}
          <div className="text-center mb-16 md:mb-20">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-3xl sm:text-4xl md:text-5xl font-bold text-secondary-900 mb-4 md:mb-6"
            >
              Our Services
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-lg md:text-xl text-secondary-600 max-w-3xl mx-auto leading-relaxed"
            >
              We help executives, boards, and investors tie data to strategy. You'll defend your decisions 
              in the boardroom and know where, how, and why to spend and when to change direction.
            </motion.p>
          </div>

          {/* Services */}
          <div className="space-y-16 md:space-y-20 mb-16 md:mb-20">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center ${
                  index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                      <SafeIcon icon={service.icon} className="w-5 h-5 md:w-6 md:h-6 text-primary-600" />
                    </div>
                    <div>
                      <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-secondary-900">{service.title}</h2>
                    </div>
                  </div>
                  <p className="text-base md:text-lg text-primary-600 font-medium mb-4">{service.subtitle}</p>
                  <p className="text-secondary-600 mb-4 md:mb-6 leading-relaxed text-sm md:text-base">{service.description}</p>
                  <div className="bg-secondary-50 p-3 md:p-4 rounded-lg mb-4">
                    <h4 className="font-semibold text-secondary-900 mb-2 text-sm md:text-base">Typical Outcomes:</h4>
                    <p className="text-secondary-600 text-xs md:text-sm">{service.outcomes}</p>
                  </div>
                </div>
                <div className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                  <div className="bg-white p-4 md:p-6 rounded-xl shadow-sm border border-secondary-100">
                    <h3 className="font-semibold text-secondary-900 mb-4 text-sm md:text-base">What We Deliver:</h3>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start space-x-2">
                          <SafeIcon icon={FiCheckCircle} className="w-4 h-4 md:w-5 md:h-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-secondary-600 text-xs md:text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Approach */}
          <div className="bg-secondary-50 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-12 md:py-20 mb-16 md:mb-20">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12 md:mb-16">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-secondary-900 mb-4 md:mb-6">How We Work</h2>
                <p className="text-base md:text-lg text-secondary-600 max-w-3xl mx-auto leading-relaxed">
                  Our approach combines rigorous analysis with practical implementation, 
                  always centered on your business outcomes and shared values.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                {approach.map((item, index) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="text-center"
                  >
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <SafeIcon icon={item.icon} className="w-6 h-6 md:w-8 md:h-8 text-primary-600" />
                    </div>
                    <h3 className="text-base md:text-lg font-semibold text-secondary-900 mb-2">{item.title}</h3>
                    <p className="text-secondary-600 text-xs md:text-sm leading-relaxed">{item.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {/* Track Record */}
          <div className="text-center mb-16 md:mb-20">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-secondary-900 mb-4 md:mb-6">Our Track Record</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-4xl mx-auto">
              <div className="bg-white p-4 md:p-6 rounded-xl shadow-sm border border-secondary-100">
                <div className="text-2xl md:text-3xl font-bold text-primary-600 mb-2">12-20%</div>
                <div className="text-secondary-600 font-medium mb-2 text-sm md:text-base">ROI Improvements</div>
                <div className="text-secondary-500 text-xs md:text-sm">Through data-backed decision frameworks</div>
              </div>
              <div className="bg-white p-4 md:p-6 rounded-xl shadow-sm border border-secondary-100">
                <div className="text-2xl md:text-3xl font-bold text-primary-600 mb-2">35%</div>
                <div className="text-secondary-600 font-medium mb-2 text-sm md:text-base">Faster Planning</div>
                <div className="text-secondary-500 text-xs md:text-sm">Streamlined cycles with living tools</div>
              </div>
              <div className="bg-white p-4 md:p-6 rounded-xl shadow-sm border border-secondary-100">
                <div className="text-2xl md:text-3xl font-bold text-primary-600 mb-2">7-Figure</div>
                <div className="text-secondary-600 font-medium mb-2 text-sm md:text-base">Growth Wins</div>
                <div className="text-secondary-500 text-xs md:text-sm">Grounded in clarity, not guesswork</div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-primary-600 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-12 md:py-16 rounded-2xl">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 md:mb-6">
                Ready to Build Your Strategy?
              </h2>
              <p className="text-lg md:text-xl text-primary-100 mb-6 md:mb-8 leading-relaxed">
                Let's talk about where you are, where you want to go, and how to get there with clarity and confidence.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="bg-white text-primary-600 px-6 md:px-8 py-3 rounded-lg font-medium hover:bg-secondary-50 transition-colors inline-flex items-center justify-center space-x-2"
                >
                  <span>Start the Conversation</span>
                  <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
                </Link>
                <Link
                  to="/about"
                  className="border border-primary-300 text-white px-6 md:px-8 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors text-center"
                >
                  Learn About Our Approach
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Services;